// components/toolbar/Toolbar.tsx
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  validateWorkflow,
  saveWorkflow,
} from "../../../../../core/data/redux/automations/thunks";
import {
  selectActiveCompany,
  selectIsLoading,
} from "../../../../../core/data/redux/automations/selector";
import { ValidationResultsModal } from "../modals/ValidationResultsModal";
import { notify } from "../../../../../services/toast.config";
import { WORKFLOW_CATEGORY, WORKFLOW_TRIGGER_TYPE } from "../../utils/enums";
import "../../styles/Toolbar.css";

interface ToolbarProps {
  onSave: () => void;
  onLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onDeleteNode: () => void;
  hasSelectedNode: boolean;
  workflowName: string;
  onWorkflowNameChange: (name: string) => void;
  exportWorkflow: () => any; // Full workflow for validation
  exportWorkflowForBackend: () => any; // Sanitized workflow for saving
  setWorkflowId: (id: number | null) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  onSave,
  onLoad,
  onClear,
  onDeleteNode,
  hasSelectedNode,
  workflowName,
  onWorkflowNameChange,
  exportWorkflow,
  exportWorkflowForBackend,
  setWorkflowId,
}) => {
  const dispatch = useDispatch<any>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeCompany = useSelector(selectActiveCompany);
  const isLoading = useSelector(selectIsLoading);

  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);

  const handleLoadClick = () => {
    fileInputRef.current?.click();
  };

  const handleValidate = async () => {
    // Use FULL workflow for validation (needs fields for validation logic)
    const workflow = exportWorkflow();

    if (!workflow.nodes || workflow.nodes.length === 0) {
      notify.error("Workflow is empty. Add at least one node to validate.");
      return;
    }

    setValidating(true);
    try {
      let result = await dispatch(validateWorkflow({ workflow })).unwrap();
      result = result?.data;

      setValidationResult(result);
      setShowValidationModal(true);
    } catch (error: any) {
      notify.error(`Validation error: ${error.message || "Unknown error"}`);
    } finally {
      setValidating(false);
    }
  };

  const handleSaveToBackend = async () => {
    if (!workflowName || workflowName.trim() === "") {
      notify.error("Please provide a workflow name before saving.");
      return;
    }

    if (!activeCompany?.company_id) {
      notify.error("No company selected. Please select a company first.");
      return;
    }

    setSaving(true);
    try {
      // First validate using FULL workflow
      const fullWorkflow = exportWorkflow();

      if (!fullWorkflow.nodes || fullWorkflow.nodes.length === 0) {
        notify.error("Cannot save empty workflow. Add at least one node.");
        setSaving(false);
        return;
      }

      let validationResult = await dispatch(
        validateWorkflow({ workflow: fullWorkflow })
      ).unwrap();

      validationResult = validationResult?.data;

      if (!validationResult.valid) {
        const errorCount = validationResult.errors.length;
        const warningCount = validationResult.warnings.length;

        notify.error(
          `Cannot save invalid workflow. ${errorCount} error(s), ${warningCount} warning(s) found.`
        );

        validationResult.errors.slice(0, 3).forEach((err: any) => {
          notify.error(`${err.nodeLabel || "Workflow"}: ${err.message}`);
        });

        setSaving(false);
        return;
      }

      // Get SANITIZED workflow for saving
      const sanitizedWorkflow = exportWorkflowForBackend();

      // **DETECT EXECUTION TYPE FROM TRIGGER NODE**
      const detectExecutionType = (nodes: any[]): string => {
        const triggerNode = nodes.find(
          (node) => node.type === "trigger" || node.data?.type === "trigger"
        );

        if (!triggerNode) {
          return WORKFLOW_TRIGGER_TYPE.EVENT; // Default fallback
        }

        const nodeKey = triggerNode.data?.nodeKey || "";

        // Map trigger node keys to execution types
        if (nodeKey === "manual_trigger") {
          return WORKFLOW_TRIGGER_TYPE.MANUAL;
        } else if (nodeKey === "schedule_trigger") {
          return WORKFLOW_TRIGGER_TYPE.SCHEDULE;
        } else {
          return WORKFLOW_TRIGGER_TYPE.EVENT; // All other triggers are event-based
        }
      };

      const executionType = detectExecutionType(sanitizedWorkflow.nodes);

      // Prepare workflow data for backend
      const workflowData: any = {
        name: workflowName,
        description: sanitizedWorkflow.description || "",
        workflow_type:
          sanitizedWorkflow.workflow_type || WORKFLOW_CATEGORY.GENERAL,
        execution_type: executionType, // **DYNAMICALLY SET BASED ON TRIGGER**
        nodes: sanitizedWorkflow.nodes,
        edges: sanitizedWorkflow.edges,
      };

      // Only include ID if it's a valid number
      if (sanitizedWorkflow.id && typeof sanitizedWorkflow.id === "number") {
        workflowData.id = sanitizedWorkflow.id;
      }

      const result = await dispatch(
        saveWorkflow({
          companyId: activeCompany.company_id.toString(),
          companyType: activeCompany.company_type,
          workflow: workflowData,
        })
      ).unwrap();

      notify.success(
        `✓ Workflow "${workflowName}" saved successfully! Version: ${result.data.workflow.version}`
      );

      // Update workflow ID for future updates
      if (result.data.workflow.id) {
        setWorkflowId(result.data.workflow.id);
      }
    } catch (error: any) {
      notify.error(
        `Failed to save workflow: ${error.message || "Unknown error"}`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadJSON = () => {
    // For local download, trigger the onSave (downloads full workflow)
    onSave();
  };

  return (
    <div className="editor-toolbar">
      <div className="toolbar-left">
        <div className="workflow-name-container">
          <svg
            className="workflow-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <input
            type="text"
            className="workflow-name-input"
            value={workflowName}
            onChange={(e) => onWorkflowNameChange(e.target.value)}
            placeholder="Untitled Workflow"
          />
        </div>
      </div>

      <div className="toolbar-right">
        {/* Validate Button */}
        <button
          className="toolbar-btn validate"
          onClick={handleValidate}
          disabled={validating || isLoading}
          title="Validate workflow structure">
          {validating ? (
            <>
              <span className="spinner-small"></span>
              Validating...
            </>
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              Validate
            </>
          )}
        </button>

        {/* Save to Backend Button */}
        <button
          className="toolbar-btn primary"
          onClick={handleSaveToBackend}
          disabled={saving || isLoading}
          title="Save workflow to server">
          {saving ? (
            <>
              <span className="spinner-small"></span>
              Saving...
            </>
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              Save
            </>
          )}
        </button>

        {/* Download JSON Button */}
        <button
          className="toolbar-btn secondary"
          onClick={handleDownloadJSON}
          title="Download workflow as JSON">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>

        {/* Load Button */}
        <button
          className="toolbar-btn secondary"
          onClick={handleLoadClick}
          title="Load workflow from JSON">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2">
            <path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
          Import
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={onLoad}
          style={{ display: "none" }}
        />

        <div className="toolbar-divider"></div>

        {/* Delete Node Button */}
        {hasSelectedNode && (
          <button
            className="toolbar-btn danger"
            onClick={onDeleteNode}
            title="Delete selected node">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Delete
          </button>
        )}

        {/* Clear All Button */}
        <button
          className="toolbar-btn danger-outline"
          onClick={onClear}
          title="Clear all nodes">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
          Clear All
        </button>
      </div>

      <ValidationResultsModal
        visible={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        result={validationResult}
      />
    </div>
  );
};

export default Toolbar;
